/**
 * Layout Theme Presets
 * 
 * Pre-built layout templates that administrators can choose from.
 * Each preset defines a complete layout configuration including
 * navigation, hero, content layout, and footer styles.
 */

import {
  type LayoutTheme,
  type LayoutPresetId,
  DEFAULT_NAVIGATION_CONFIG,
  DEFAULT_HERO_CONFIG,
  DEFAULT_CONTENT_CONFIG,
  DEFAULT_FOOTER_CONFIG,
} from './layout-types';

// ============================================
// LAYOUT THEME PRESETS
// ============================================

export const layoutPresets: Record<LayoutPresetId, LayoutTheme> = {
  // ----------------------------------------
  // CLASSIC (Default - Current Design)
  // ----------------------------------------
  classic: {
    id: 'classic',
    name: 'Classic',
    description: 'Traditional top navigation with full-width hero. The default template layout.',
    navigation: {
      ...DEFAULT_NAVIGATION_CONFIG,
      style: 'top-bar',
      sticky: true,
      logoPosition: 'left',
    },
    hero: {
      ...DEFAULT_HERO_CONFIG,
      style: 'full-image',
      height: 'large',
      contentAlignment: 'left',
    },
    content: {
      ...DEFAULT_CONTENT_CONFIG,
      style: 'single-column',
      maxWidth: 'wide',
    },
    footer: {
      ...DEFAULT_FOOTER_CONFIG,
      style: 'full',
      columns: 4,
    },
  },

  // ----------------------------------------
  // MODERN SIDEBAR
  // ----------------------------------------
  'modern-sidebar': {
    id: 'modern-sidebar',
    name: 'Modern Sidebar',
    description: 'Fixed left sidebar navigation with spacious content area. Great for content-heavy sites.',
    navigation: {
      style: 'sidebar-left',
      sticky: true,
      transparent: false,
      showLogo: true,
      logoPosition: 'center',
    },
    hero: {
      style: 'gradient-only',
      height: 'medium',
      overlayOpacity: 0,
      contentAlignment: 'center',
      showStats: true,
      showCTA: true,
    },
    content: {
      style: 'single-column',
      maxWidth: 'wide',
      sidebarWidth: 'medium',
      showBreadcrumbs: true,
    },
    footer: {
      style: 'simple',
      showNewsletter: false,
      showSocialLinks: true,
      showQuickLinks: false,
      columns: 1,
    },
  },

  // ----------------------------------------
  // MINIMAL CLEAN
  // ----------------------------------------
  'minimal-clean': {
    id: 'minimal-clean',
    name: 'Minimal Clean',
    description: 'Clean, minimalist design with collapsible navigation. Focus on content with minimal distractions.',
    navigation: {
      style: 'sidebar-collapsible',
      sticky: true,
      transparent: true,
      showLogo: true,
      logoPosition: 'left',
    },
    hero: {
      style: 'minimal',
      height: 'small',
      overlayOpacity: 0,
      contentAlignment: 'center',
      showStats: false,
      showCTA: true,
    },
    content: {
      style: 'single-column',
      maxWidth: 'narrow',
      sidebarWidth: 'narrow',
      showBreadcrumbs: false,
    },
    footer: {
      style: 'minimal',
      showNewsletter: false,
      showSocialLinks: true,
      showQuickLinks: false,
      columns: 1,
    },
  },

  // ----------------------------------------
  // CORPORATE
  // ----------------------------------------
  corporate: {
    id: 'corporate',
    name: 'Corporate',
    description: 'Professional layout with centered logo and mega menu navigation. Ideal for businesses and organizations.',
    navigation: {
      style: 'mega-menu',
      sticky: true,
      transparent: false,
      showLogo: true,
      logoPosition: 'center',
    },
    hero: {
      style: 'split-content',
      height: 'large',
      overlayOpacity: 40,
      contentAlignment: 'left',
      showStats: true,
      showCTA: true,
    },
    content: {
      style: 'two-column-right',
      maxWidth: 'wide',
      sidebarWidth: 'narrow',
      showBreadcrumbs: true,
    },
    footer: {
      style: 'full',
      showNewsletter: true,
      showSocialLinks: true,
      showQuickLinks: true,
      columns: 4,
    },
  },

  // ----------------------------------------
  // MAGAZINE
  // ----------------------------------------
  magazine: {
    id: 'magazine',
    name: 'Magazine',
    description: 'Editorial-style layout with carousel hero and card-based content. Perfect for news and blog sites.',
    navigation: {
      style: 'centered-split',
      sticky: true,
      transparent: false,
      showLogo: true,
      logoPosition: 'center',
    },
    hero: {
      style: 'carousel',
      height: 'medium',
      overlayOpacity: 50,
      contentAlignment: 'center',
      showStats: false,
      showCTA: true,
    },
    content: {
      style: 'magazine',
      maxWidth: 'wide',
      sidebarWidth: 'medium',
      showBreadcrumbs: true,
    },
    footer: {
      style: 'full',
      showNewsletter: true,
      showSocialLinks: true,
      showQuickLinks: true,
      columns: 3,
    },
  },

  // ----------------------------------------
  // LANDING PAGE
  // ----------------------------------------
  'landing-page': {
    id: 'landing-page',
    name: 'Landing Page',
    description: 'Marketing-focused layout with full-screen hero and bold CTAs. Designed for conversions.',
    navigation: {
      style: 'top-bar',
      sticky: true,
      transparent: true,
      showLogo: true,
      logoPosition: 'left',
    },
    hero: {
      style: 'full-image',
      height: 'full',
      overlayOpacity: 70,
      contentAlignment: 'center',
      showStats: true,
      showCTA: true,
    },
    content: {
      style: 'single-column',
      maxWidth: 'medium',
      sidebarWidth: 'medium',
      showBreadcrumbs: false,
    },
    footer: {
      style: 'centered',
      showNewsletter: true,
      showSocialLinks: true,
      showQuickLinks: true,
      columns: 3,
    },
  },

  // ----------------------------------------
  // DASHBOARD
  // ----------------------------------------
  dashboard: {
    id: 'dashboard',
    name: 'Dashboard',
    description: 'App-like layout with collapsible sidebar. Ideal for member portals and interactive sites.',
    navigation: {
      style: 'sidebar-collapsible',
      sticky: true,
      transparent: false,
      showLogo: true,
      logoPosition: 'left',
    },
    hero: {
      style: 'minimal',
      height: 'small',
      overlayOpacity: 0,
      contentAlignment: 'left',
      showStats: false,
      showCTA: false,
    },
    content: {
      style: 'card-grid',
      maxWidth: 'full',
      sidebarWidth: 'narrow',
      showBreadcrumbs: true,
    },
    footer: {
      style: 'minimal',
      showNewsletter: false,
      showSocialLinks: false,
      showQuickLinks: true,
      columns: 1,
    },
  },

  // ----------------------------------------
  // PORTFOLIO
  // ----------------------------------------
  portfolio: {
    id: 'portfolio',
    name: 'Portfolio',
    description: 'Creative portfolio layout with visual focus. Perfect for artists, designers, and creative professionals.',
    navigation: {
      style: 'top-bar',
      sticky: true,
      transparent: true,
      showLogo: true,
      logoPosition: 'center',
    },
    hero: {
      style: 'full-image',
      height: 'full',
      overlayOpacity: 40,
      contentAlignment: 'center',
      showStats: false,
      showCTA: true,
    },
    content: {
      style: 'card-grid',
      maxWidth: 'wide',
      sidebarWidth: 'medium',
      showBreadcrumbs: false,
    },
    footer: {
      style: 'centered',
      showNewsletter: false,
      showSocialLinks: true,
      showQuickLinks: true,
      columns: 2,
    },
  },

  // ----------------------------------------
  // SAAS PRODUCT
  // ----------------------------------------
  'saas-product': {
    id: 'saas-product',
    name: 'SaaS Product',
    description: 'Modern SaaS/product page layout. Clean, professional design optimized for product showcases and conversions.',
    navigation: {
      style: 'top-bar',
      sticky: true,
      transparent: false,
      showLogo: true,
      logoPosition: 'left',
    },
    hero: {
      style: 'split-content',
      height: 'large',
      overlayOpacity: 0,
      contentAlignment: 'left',
      showStats: true,
      showCTA: true,
    },
    content: {
      style: 'single-column',
      maxWidth: 'medium',
      sidebarWidth: 'medium',
      showBreadcrumbs: false,
    },
    footer: {
      style: 'full',
      showNewsletter: true,
      showSocialLinks: true,
      showQuickLinks: true,
      columns: 4,
    },
  },

  // ----------------------------------------
  // E-COMMERCE
  // ----------------------------------------
  ecommerce: {
    id: 'ecommerce',
    name: 'E-Commerce',
    description: 'E-commerce optimized layout with product-focused navigation and grid-based content. Ideal for online stores.',
    navigation: {
      style: 'mega-menu',
      sticky: true,
      transparent: false,
      showLogo: true,
      logoPosition: 'left',
    },
    hero: {
      style: 'carousel',
      height: 'medium',
      overlayOpacity: 30,
      contentAlignment: 'center',
      showStats: false,
      showCTA: true,
    },
    content: {
      style: 'card-grid',
      maxWidth: 'wide',
      sidebarWidth: 'narrow',
      showBreadcrumbs: true,
    },
    footer: {
      style: 'full',
      showNewsletter: true,
      showSocialLinks: true,
      showQuickLinks: true,
      columns: 4,
    },
  },

  // ----------------------------------------
  // NONPROFIT
  // ----------------------------------------
  nonprofit: {
    id: 'nonprofit',
    name: 'Nonprofit',
    description: 'Mission-driven layout with emotional storytelling focus. Designed for nonprofits and charitable organizations.',
    navigation: {
      style: 'top-bar',
      sticky: true,
      transparent: true,
      showLogo: true,
      logoPosition: 'left',
    },
    hero: {
      style: 'full-image',
      height: 'full',
      overlayOpacity: 60,
      contentAlignment: 'center',
      showStats: true,
      showCTA: true,
    },
    content: {
      style: 'single-column',
      maxWidth: 'medium',
      sidebarWidth: 'medium',
      showBreadcrumbs: false,
    },
    footer: {
      style: 'full',
      showNewsletter: true,
      showSocialLinks: true,
      showQuickLinks: true,
      columns: 4,
    },
  },

  // ----------------------------------------
  // EDUCATION
  // ----------------------------------------
  education: {
    id: 'education',
    name: 'Education',
    description: 'Learning platform layout with clear navigation and structured content. Perfect for schools, courses, and educational content.',
    navigation: {
      style: 'sidebar-left',
      sticky: true,
      transparent: false,
      showLogo: true,
      logoPosition: 'center',
    },
    hero: {
      style: 'gradient-only',
      height: 'medium',
      overlayOpacity: 0,
      contentAlignment: 'center',
      showStats: true,
      showCTA: true,
    },
    content: {
      style: 'two-column-right',
      maxWidth: 'wide',
      sidebarWidth: 'medium',
      showBreadcrumbs: true,
    },
    footer: {
      style: 'full',
      showNewsletter: true,
      showSocialLinks: true,
      showQuickLinks: true,
      columns: 3,
    },
  },

  // ----------------------------------------
  // HEALTHCARE
  // ----------------------------------------
  healthcare: {
    id: 'healthcare',
    name: 'Healthcare',
    description: 'Trust-focused, clean medical layout. Professional design that conveys reliability and care.',
    navigation: {
      style: 'top-bar',
      sticky: true,
      transparent: false,
      showLogo: true,
      logoPosition: 'left',
    },
    hero: {
      style: 'split-content',
      height: 'large',
      overlayOpacity: 20,
      contentAlignment: 'left',
      showStats: true,
      showCTA: true,
    },
    content: {
      style: 'two-column-right',
      maxWidth: 'wide',
      sidebarWidth: 'narrow',
      showBreadcrumbs: true,
    },
    footer: {
      style: 'full',
      showNewsletter: false,
      showSocialLinks: true,
      showQuickLinks: true,
      columns: 4,
    },
  },

  // ----------------------------------------
  // RESTAURANT
  // ----------------------------------------
  restaurant: {
    id: 'restaurant',
    name: 'Restaurant',
    description: 'Visual, appetizing layout perfect for restaurants and food services. Emphasizes imagery and menu presentation.',
    navigation: {
      style: 'centered-split',
      sticky: true,
      transparent: true,
      showLogo: true,
      logoPosition: 'center',
    },
    hero: {
      style: 'full-image',
      height: 'full',
      overlayOpacity: 50,
      contentAlignment: 'center',
      showStats: false,
      showCTA: true,
    },
    content: {
      style: 'magazine',
      maxWidth: 'wide',
      sidebarWidth: 'medium',
      showBreadcrumbs: false,
    },
    footer: {
      style: 'centered',
      showNewsletter: true,
      showSocialLinks: true,
      showQuickLinks: true,
      columns: 3,
    },
  },

  // ----------------------------------------
  // AGENCY
  // ----------------------------------------
  agency: {
    id: 'agency',
    name: 'Creative Agency',
    description: 'Bold, creative agency layout with modern aesthetics. Perfect for design studios and creative agencies.',
    navigation: {
      style: 'top-bar',
      sticky: true,
      transparent: true,
      showLogo: true,
      logoPosition: 'left',
    },
    hero: {
      style: 'full-image',
      height: 'full',
      overlayOpacity: 30,
      contentAlignment: 'left',
      showStats: false,
      showCTA: true,
    },
    content: {
      style: 'card-grid',
      maxWidth: 'wide',
      sidebarWidth: 'medium',
      showBreadcrumbs: false,
    },
    footer: {
      style: 'simple',
      showNewsletter: false,
      showSocialLinks: true,
      showQuickLinks: true,
      columns: 2,
    },
  },

  // ----------------------------------------
  // COMMUNITY
  // ----------------------------------------
  community: {
    id: 'community',
    name: 'Community',
    description: 'Social, engagement-focused layout. Designed for community organizations, forums, and social platforms.',
    navigation: {
      style: 'mega-menu',
      sticky: true,
      transparent: false,
      showLogo: true,
      logoPosition: 'left',
    },
    hero: {
      style: 'gradient-only',
      height: 'medium',
      overlayOpacity: 0,
      contentAlignment: 'center',
      showStats: true,
      showCTA: true,
    },
    content: {
      style: 'three-column',
      maxWidth: 'wide',
      sidebarWidth: 'narrow',
      showBreadcrumbs: true,
    },
    footer: {
      style: 'full',
      showNewsletter: true,
      showSocialLinks: true,
      showQuickLinks: true,
      columns: 4,
    },
  },

  // ----------------------------------------
  // BLOG FOCUS
  // ----------------------------------------
  'blog-focus': {
    id: 'blog-focus',
    name: 'Blog Focus',
    description: 'Content-first blog layout optimized for reading. Clean typography and article-focused design.',
    navigation: {
      style: 'top-bar',
      sticky: true,
      transparent: false,
      showLogo: true,
      logoPosition: 'left',
    },
    hero: {
      style: 'minimal',
      height: 'small',
      overlayOpacity: 0,
      contentAlignment: 'center',
      showStats: false,
      showCTA: false,
    },
    content: {
      style: 'two-column-right',
      maxWidth: 'medium',
      sidebarWidth: 'narrow',
      showBreadcrumbs: true,
    },
    footer: {
      style: 'simple',
      showNewsletter: true,
      showSocialLinks: true,
      showQuickLinks: true,
      columns: 2,
    },
  },
};

// ============================================
// HELPER FUNCTIONS
// ============================================

/**
 * Get a layout preset by ID
 */
export const getLayoutPreset = (presetId: LayoutPresetId): LayoutTheme => {
  return layoutPresets[presetId] || layoutPresets.classic;
};

/**
 * Get all available layout presets
 */
export const getAllLayoutPresets = (): LayoutTheme[] => {
  return Object.values(layoutPresets);
};

/**
 * Get preset IDs for selection UI
 */
export const getLayoutPresetOptions = (): { id: LayoutPresetId; name: string; description: string }[] => {
  return Object.values(layoutPresets).map(preset => ({
    id: preset.id as LayoutPresetId,
    name: preset.name,
    description: preset.description,
  }));
};

/**
 * Check if a preset ID is valid
 */
export const isValidPresetId = (id: string): id is LayoutPresetId => {
  return id in layoutPresets;
};

/**
 * Get the default preset
 */
export const getDefaultPreset = (): LayoutTheme => {
  return layoutPresets.classic;
};




